import { getJSON, postJSON } from 'common/services/api'
import { useCallback, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import {
  RESIDENT_NEEDS_QUERY_KEY,
  type ResidentNeedV6
} from './useResidentNeeds'

export const NEED_SUGGESTIONS_QUERY_KEY = 'residentNeedSuggestions'

type UserPartial = {
  id: string
  name: string
  picture?: string
}

export type NeedSuggestionStatus = 'pending' | 'accepted' | 'rejected'

export type NeedSuggestion = {
  id: string
  residentId: string
  initialAssessment: {
    id: string
    completedBy?: UserPartial
    completedOn?: string
    form: {
      id: string
      name: string
      iconText?: string
      color?: string
    }
  }
  carePlanAdl: {
    id: string
    name: string
  }
  modsCategory: string
  need: string
  note?: string
  confidence: number
  status: NeedSuggestionStatus
  generatedOn: string
  acceptedOn?: string
  acceptedBy?: UserPartial
  rejectedOn?: string
  rejectedBy?: UserPartial
  resultingNeedId?: string
  resultingResidentNeedId?: string
}

export type NeedSuggestionAcceptResult = {
  suggestion: NeedSuggestion
  /**
   * The created-or-reused resident need in the resident-needs list-item
   * shape. The backend deliberately does NOT link the care plan — appending
   * residentNeedId to the review's residentNeedIds is the caller's job.
   */
  residentNeed: ResidentNeedV6
  needId: string
  residentNeedId: string
  carePlanAdlId: string
}

export type SuggestionDismissReason =
  | 'not_relevant'
  | 'already_recorded'
  | 'inaccurate'
  | 'wording'
  | 'duplicate'
  | 'not_appropriate'

/**
 * A locally staged decision on a suggestion. Nothing is sent to the server
 * until the care-plan review is saved (commitSuggestionDecisions); Cancel or
 * navigating away discards the decision and the suggestion stays pending.
 */
export type SuggestionDecision = {
  suggestion: NeedSuggestion
  action: 'add' | 'dismiss'
  /** When the decision was staged, for the draft row's "Added {{date}}…" line. */
  stagedOn?: string
  /** Set when the user edited the note before adding (Edit note). */
  editedNote?: string
  dismissReason?: SuggestionDismissReason
  /** Set once the accept POST succeeded, so a retried save never double-accepts. */
  committedResidentNeedId?: string
  /** Set once the edited note has been written to the resident need. */
  noteCommitted?: boolean
  /** Set once a dismiss has been committed server-side. */
  committed?: boolean
}

export type DecisionCommitPatch = Partial<
  Pick<
    SuggestionDecision,
    'committedResidentNeedId' | 'noteCommitted' | 'committed'
  >
>

const suggestionsUrl = (
  residentId: string,
  carePlanAdlId?: string,
  status?: NeedSuggestionStatus
) => {
  const params = new URLSearchParams()
  if (carePlanAdlId) params.set('carePlanAdlId', carePlanAdlId)
  if (status) params.set('status', status)
  const query = params.toString()
  return `/v6/residents/${residentId}/suggested-needs${query ? `?${query}` : ''}`
}

export const useNeedSuggestions = ({
  residentId,
  carePlanAdlId,
  status,
  enabled = true
}: {
  residentId: string
  carePlanAdlId?: string
  status?: NeedSuggestionStatus
  enabled?: boolean
}) => {
  const { data, isLoading, isError, refetch } = useQuery(
    [
      NEED_SUGGESTIONS_QUERY_KEY,
      residentId,
      carePlanAdlId ?? null,
      status ?? 'pending'
    ],
    async () => {
      const result = (await getJSON({
        url: suggestionsUrl(residentId, carePlanAdlId, status)
      })) as { data: NeedSuggestion[] }
      return result.data ?? []
    },
    { enabled: enabled && !!residentId }
  )

  return { suggestions: data ?? [], isLoading, isError, refetch }
}

/**
 * Resident-need ids that originated from an accepted AI suggestion, used to
 * mark those needs as AI-assisted wherever they are listed.
 */
export const useAcceptedSuggestionNeedIds = ({
  residentId,
  enabled = true
}: {
  residentId: string
  enabled?: boolean
}): Set<string> => {
  const { suggestions } = useNeedSuggestions({
    residentId,
    status: 'accepted',
    enabled
  })

  return useMemo(
    () =>
      new Set(
        suggestions
          .map((s) => s.resultingResidentNeedId)
          .filter((id): id is string => !!id)
      ),
    [suggestions]
  )
}

/**
 * "Don't show this again": permanently rejects the given suggestions for the
 * resident, immediately (this is deliberately NOT staged — the design's
 * acceptance criteria require the choice to survive reloads and apply to
 * everyone viewing the resident). Newly generated suggestions can still
 * appear later; true per-resident suppression needs the CM API soft-delete
 * (separate ticket), and this is the seam to swap to it.
 */
export const useNeedSuggestionRejectAll = (residentId: string) => {
  const queryClient = useQueryClient()
  const [isRejecting, setIsRejecting] = useState(false)

  const rejectAll = useCallback(
    async (suggestionIds: string[]): Promise<void> => {
      setIsRejecting(true)
      try {
        for (const suggestionId of suggestionIds) {
          await postJSON({
            url: `/v6/residents/${residentId}/suggested-needs/${suggestionId}/reject`,
            json: {}
          })
        }
      } finally {
        setIsRejecting(false)
        queryClient.invalidateQueries([NEED_SUGGESTIONS_QUERY_KEY, residentId])
      }
    },
    [queryClient, residentId]
  )

  return { rejectAll, isRejecting }
}

/**
 * Commits staged suggestion decisions as part of saving a care-plan review.
 *
 * Accepts run first (their resulting resident-need ids go into the review
 * payload so the plan link is saved atomically with the review), then
 * dismissals. Progress is reported per decision through onDecisionCommitted
 * so the caller can persist it into its draft state — a failed save can then
 * be retried without re-committing what already succeeded.
 *
 * Returns the resident-need ids of every accepted decision (including ones
 * committed on an earlier, failed attempt). Throws on the first failure.
 */
export const useNeedSuggestionCommit = (residentId: string) => {
  const queryClient = useQueryClient()
  const [isCommitting, setIsCommitting] = useState(false)

  const commitDecisions = useCallback(
    async (
      decisions: SuggestionDecision[],
      onDecisionCommitted: (
        suggestionId: string,
        patch: DecisionCommitPatch
      ) => void
    ): Promise<string[]> => {
      setIsCommitting(true)
      try {
        const acceptedResidentNeedIds: string[] = []

        for (const decision of decisions.filter((d) => d.action === 'add')) {
          let residentNeedId = decision.committedResidentNeedId
          let noteCommitted = decision.noteCommitted ?? false

          if (!residentNeedId) {
            const result = (await postJSON({
              url: `/v6/residents/${residentId}/suggested-needs/${decision.suggestion.id}/accept`,
              json: {}
            })) as { data: NeedSuggestionAcceptResult }
            residentNeedId = result.data.residentNeedId
            noteCommitted = decision.editedNote === undefined
            onDecisionCommitted(decision.suggestion.id, {
              committedResidentNeedId: residentNeedId,
              noteCommitted
            })
          }

          if (decision.editedNote !== undefined && !noteCommitted) {
            await postJSON({
              url: `/v6/residents/${residentId}/needs/${residentNeedId}`,
              method: 'PUT',
              json: { notes: decision.editedNote }
            })
            onDecisionCommitted(decision.suggestion.id, {
              noteCommitted: true
            })
          }

          acceptedResidentNeedIds.push(residentNeedId)
        }

        for (const decision of decisions.filter(
          (d) => d.action === 'dismiss'
        )) {
          if (decision.committed) continue
          // reason is forward-compatibility: the endpoint takes no body today
          // (extra fields are ignored); a backend follow-up will store it for
          // the suggestion-quality feedback loop.
          await postJSON({
            url: `/v6/residents/${residentId}/suggested-needs/${decision.suggestion.id}/reject`,
            json: decision.dismissReason
              ? { reason: decision.dismissReason }
              : {}
          })
          onDecisionCommitted(decision.suggestion.id, { committed: true })
        }

        return acceptedResidentNeedIds
      } finally {
        setIsCommitting(false)
        queryClient.invalidateQueries([NEED_SUGGESTIONS_QUERY_KEY, residentId])
        queryClient.invalidateQueries([RESIDENT_NEEDS_QUERY_KEY, residentId])
        // Legacy (non-v6) resident needs list, still used when
        // mods_needs_resident_profile is off.
        queryClient.invalidateQueries(['resident-needs', residentId])
      }
    },
    [queryClient, residentId]
  )

  return { commitDecisions, isCommitting }
}
